import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'

import type { TDefaultButtonProps } from '@/src/elements/button/default'
import { ModalInput, type TModalInputProps } from '@/src/elements/input/ModalInput/ModalInput'
import { Message } from '@/src/elements/message'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { htmlTagsMap, useTranslate } from '@/src/lang/useTranslate'

import { useClearMailDeliveryIssuesMutation } from '../../../../services/useClearMailDeliveryIssuesMutation'

export const MailDeliveryIssueAlert = ({
  children,
  onUncaughtSubmit,
}: Pick<TModalInputProps, 'onUncaughtSubmit' | 'children'>): ReactNode => {
  const { data: me } = useMeQuery()
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { navigate } = useRouter()

  const { mutate: clearMailDeliveryIssues } = useClearMailDeliveryIssuesMutation()

  if (!me?.mailDeliveryIssues.hardBounce && !me?.mailDeliveryIssues.invalid && !me?.mailDeliveryIssues.spamReport)
    return null

  const cannotDeliver = me?.mailDeliveryIssues.hardBounce || me?.mailDeliveryIssues.invalid

  const spamReportButton: TDefaultButtonProps = {
    startIcon: ICON.CHECK,
    children: t('words.done'),
    onPress: () => clearMailDeliveryIssues(),
  }
  const reportIssueButton: TDefaultButtonProps = {
    children: t('app.report-issue.button'),
    onPress: () => navigate(routes.contact()),
    variant: 'light',
  }

  return (
    <ModalInput
      onUncaughtSubmit={async () => {
        // Will throw if email is not valid
        await onUncaughtSubmit?.()
        // If onUncaughtSubmit resolves, clear mail delivery issues
        clearMailDeliveryIssues()
        // If clearMailDeliveryIssues fails, do nothing: the message will be displayed again
      }}
      title={t('words.your-email-address')}
      renderTrigger={({ onTrigger }) => {
        const verifyEmailButton: TDefaultButtonProps = {
          startIcon: ICON.PENCIL,
          children: t('user.delivery-issue-email.verify-email'),
          onPress: onTrigger,
        }

        return (
          <Message
            colors="danger"
            iconName={ICON.MESSAGE_INFO}
            buttons={[cannotDeliver ? verifyEmailButton : spamReportButton, reportIssueButton]}
          >
            {t(
              cannotDeliver ? 'user.delivery-issue-email.hard-bounce-invalid' : 'user.delivery-issue-email.spam-report',
              htmlTagsMap,
            )}
          </Message>
        )
      }}
    >
      {children}
    </ModalInput>
  )
}
